import type { IBlock, ICluster } from "@agrigate/location-types";
import useAsyncTask from "@agrigate/shared/lib/hooks/use-async-task";
import { ensureNN } from "@agrigate/ts-utils/lib/coercions";
import type { MaybeN } from "@agrigate/ts-utils/lib/types";
import type { Combobox, ComboboxRootProps } from "@chakra-ui/react";
import { compact } from "lodash";
import { useEffect, useMemo } from "react";
import ComboboxSelect from "./ComboSelect";
import { getRPC } from "./utils/rpc";

export interface Block {
    blockId: string;
    blockName: string;
}

export default function BlockDropDown(p: {
    name?: string;
    districtId?: MaybeN<string>;
    blockId?: MaybeN<string>;
    defaultValues?: MaybeN<string[]>;
    onClusterInit?: (cluster: ICluster) => void;
    onChange?: (
        blockId?: string,
        blockName?: string,
        districtId?: string,
    ) => void;
    autofetchInitial?: MaybeN<boolean>;
    disabled?: boolean;
    width?: string;
    allowCreation?: boolean;
    placement?: ComboboxRootProps["placeContent"];
    allowFetchAllBlocks?: boolean;
    omitBlockIds?: MaybeN<string[]>;
    setDefaultValues?: (blocks: Block[]) => void;
    triggerFetchAll?: boolean;
    portalled?: boolean;
    isDesktop?: boolean;
    comboBoxProps?: Omit<Combobox.ControlProps, "children">;
    comboInputProps?: Omit<Combobox.InputProps, "children">;
    comboRootProps?: Omit<
        Combobox.RootProps,
        | "children"
        | "collection"
        | "onInputValueChange"
        | "value"
        | "onValueChange"
    >;
}) {
    const fetchBlocks = useAsyncTask(
        async () => {
            if (!p.districtId && !p.allowFetchAllBlocks) return;
            return getRPC().location.findAllBlocks({
                filters: {
                    districtId: { eq: p.districtId },
                },
            });
        },
        {
            deps: [p.districtId],
            autoRun: true,
        },
    );

    const blocks: MaybeN<IBlock[]> = fetchBlocks.data?.blocks;

    const blockOptions = useMemo(() => {
        if (!blocks) return [];
        const options: {
            value: string;
            label: string;
            item: MaybeN<IBlock>;
        }[] = blocks
            .filter(block => !p.defaultValues?.includes(block.id))
            .map(block => ({
                value: block.id,
                label: p.isDesktop ? block.name : block.name.slice(0, 30),
                item: block,
            }));
        if (!p.allowCreation || !p.districtId) return options;
        return options.concat({
            value: "$new",
            label: "New Block",
            item: null,
        });
    }, [blocks, p.allowCreation, p.defaultValues]);

    useEffect(() => {
        if (p.triggerFetchAll && blocks) {
            p.setDefaultValues?.(
                blocks.map(block => ({
                    blockId: block.id,
                    blockName: block.name,
                })),
            );
        }
    }, [p.triggerFetchAll]);

    useEffect(() => {
        if (p.defaultValues && blocks?.length) {
            const selectedBlocks = p.defaultValues.map(blockId => {
                const block = blocks?.find(_ => _.id === blockId);
                if (block)
                    return {
                        blockId: block?.id,
                        blockName: block?.name,
                    };
                else return null;
            });
            p.setDefaultValues?.(compact(selectedBlocks));
        }
    }, [blocks]);

    return (
        <ComboboxSelect
            placeholder="Select Block"
            // Handle disabled state (Chakra's Combobox.Root or Input typically handles disabled)
            comboRootProps={{
                disabled:
                    p.disabled || (!p.districtId && !p.allowFetchAllBlocks),
                ...p.comboRootProps,
            }}
            value={p.blockId ? [p.blockId] : []}
            items={
                blockOptions?.map(opt => ({
                    label: opt.label,
                    value: opt.value,
                })) ?? []
            }
            onChange={async selectedIds => {
                const selectedId = selectedIds[0];
                let block: MaybeN<IBlock>;

                if (selectedId === "$new") {
                    const name = prompt("Enter block name")?.trim();
                    if (!name) return;

                    ({ block } = await getRPC().location.addBlock({
                        name,
                        districtId: ensureNN(p.districtId),
                    }));
                } else {
                    block = selectedId
                        ? blocks?.find(b => b.id === selectedId)
                        : null;
                }
                p.onChange?.(
                    block?.id,
                    block?.name,
                    block?.districtId ?? undefined,
                );
                fetchBlocks.trigger();
            }}
            width={p.width ?? "20%"}
            placement={p.placement}
            portalled={p.portalled ?? true}
            comboBoxProps={{ ...p.comboBoxProps }}
            comboInputProps={{ ...p.comboInputProps }}
        />
    );
}
